/**
 * Environment jobs as release-timeline log lines: one per job, oldest first,
 * each carrying who asked, when, what the set did, and the outcome.
 */
import type { EnvironmentDiff, EnvironmentJobOut } from '@optum/api-client/types';

import { absoluteDateTime } from '../../lib/format.js';

export interface TimelineLine {
  text: string;
  meta: string;
  tone: 'ok' | 'warn' | 'danger' | 'info' | 'neutral';
}

/** `+httpx 0.27.0, requests 2.31.0 → 2.20.0, −numpy 2.1.0`, or a note when nothing moved. */
export function describeDiff(diff: EnvironmentDiff | null): string {
  if (!diff) return '';
  const parts = [
    ...diff.added.map((e) => `+${e.name} ${e.version}`),
    ...diff.changed.map((e) => `${e.name} ${e.from} → ${e.to}`),
    ...diff.removed.map((e) => `−${e.name} ${e.version}`),
  ];
  return parts.length > 0 ? parts.join(', ') : 'nothing moved';
}

function duration(job: EnvironmentJobOut): string {
  if (job.finished_at === null) return 'running';
  const seconds = Math.round((Date.parse(job.finished_at) - Date.parse(job.started_at)) / 1000);
  return `${Math.max(0, seconds)}s`;
}

function tone(job: EnvironmentJobOut): TimelineLine['tone'] {
  if (job.state === 'failed') return 'danger';
  if (job.state === 'succeeded') return 'ok';
  return 'info';
}

/** The jobs arrive newest first from the API; the timeline reads oldest first. */
export function environmentJobLines(jobs: EnvironmentJobOut[]): TimelineLine[] {
  return [...jobs].reverse().map((job) => {
    const outcome =
      job.state === 'failed'
        ? (job.error ?? 'failed').split('\n').at(-1) ?? 'failed'
        : job.state === 'succeeded'
          ? describeDiff(job.diff)
          : `${job.entries.length} entr${job.entries.length === 1 ? 'y' : 'ies'}`;
    const refresh = job.refresh ? ' · refresh' : '';
    return {
      text: `environment — ${job.state}`,
      meta: `${duration(job)} · ${job.backend ?? 'resolver'}${refresh} · by ${job.requested_by} · ${absoluteDateTime(job.started_at)} · ${outcome}`,
      tone: tone(job),
    };
  });
}
